import RuneSpinner from './RuneSpinner';
import { GOLD } from '../lib/medievalTheme';

/**
 * Medieval-styled placeholder shown behind a card image while the network
 * fetch is in flight. Fills its positioned parent and centers a slowly
 * rotating RuneSpinner, plus a soft gold gradient and subtle edge
 * ornamentation so it reads as an empty card frame rather than a void.
 *
 * Usage: position relatively in the card container and overlay the actual
 * <img> on top with `opacity: imgLoaded ? 1 : 0` so the placeholder
 * fades out as the image fades in.
 */
export default function CardImagePlaceholder({ spinnerSize = 36, variant = 'spinner', className = '', style = {} }) {
  return (
    <div
      aria-hidden="true"
      className={`absolute inset-0 flex items-center justify-center rounded-card overflow-hidden ${className}`}
      style={{
        background: `radial-gradient(ellipse at center, ${GOLD} 0.05) 0%, rgba(18,14,8,0.9) 70%, rgba(10,8,6,0.95) 100%)`,
        border: `1px solid ${GOLD} 0.12)`,
        boxShadow: `inset 0 0 24px rgba(0,0,0,0.6), inset 0 0 1px ${GOLD} 0.18)`,
        ...style,
      }}
    >
      {/* Faint diagonal sheen so the panel doesn't read as flat black */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `linear-gradient(135deg, transparent 40%, ${GOLD} 0.03) 50%, transparent 60%)`,
        }}
      />
      {variant === 'skeleton' ? <CardSkeleton /> : <RuneSpinner size={spinnerSize} />}
    </div>
  );
}

/**
 * Grid-scale loading state: a wireframe of a Sorcery card (name plate,
 * art window, type line, rules text) built from shimmering "bones".
 * each block's fill IS the moving gradient, the way Motion's skeleton
 * does it, rather than a flat block with a highlight passing over.
 *
 * Not a spinner: a card grid mounts dozens of these at once, and dozens
 * of rotating rune SVGs read as a stuck loading screen. Bones read as
 * "the card is arriving".
 */
function CardSkeleton() {
  return (
    <div className="absolute inset-0">
      <div className="card-skeleton-bar" style={{ top: '6%', left: '10%', right: '10%', height: '7%' }} />
      <div className="card-skeleton-bar" style={{ top: '17%', left: '7%', right: '7%', height: '42%', borderRadius: 'var(--radius-sm)' }} />
      <div className="card-skeleton-bar" style={{ top: '63%', left: '10%', right: '10%', height: '6%' }} />
      <div className="card-skeleton-bar" style={{ top: '73%', left: '12%', right: '12%', height: '4%' }} />
      <div className="card-skeleton-bar" style={{ top: '80%', left: '12%', right: '24%', height: '4%' }} />
    </div>
  );
}
